import { useEffect, useState, useCallback } from "react";

export interface NotebookEntry {
  id: string;
  timestamp: number;
  station: "mixing" | "ph" | "flame" | "titration";
  title: string;
  reactants?: string[];
  products?: string[];
  equation?: string;
  observation?: string;
  reactionType?: string;
  energy?: "exothermic" | "endothermic";
  // pH / titration / flame fields
  substance?: string;
  pH?: number;
  metalIon?: string;
  flameColor?: string;
  // Titration extras
  titrationData?: {
    acid: string;
    base: string;
    volumeAcid: number; // mL
    concAcid: number; // M
    concBase: number; // M
    equivalenceVolume: number; // mL
    finalVolume: number; // mL
    finalPH: number;
  };
  notes?: string;
}

const STORAGE_KEY = "elementpad.labNotebook.v1";

function load(): NotebookEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function save(entries: NotebookEntry[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // storage full or unavailable - fail silently
  }
}

// Cross-instance sync via a custom event
const NOTEBOOK_EVENT = "elementpad:notebook-changed";

export function useLabNotebook() {
  const [entries, setEntries] = useState<NotebookEntry[]>(() => load());

  useEffect(() => {
    const sync = () => setEntries(load());
    window.addEventListener(NOTEBOOK_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(NOTEBOOK_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const addEntry = useCallback((entry: Omit<NotebookEntry, "id" | "timestamp">) => {
    const next: NotebookEntry = {
      ...entry,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: Date.now(),
    };
    const all = [next, ...load()].slice(0, 200); // cap at 200 entries
    save(all);
    window.dispatchEvent(new Event(NOTEBOOK_EVENT));
    return next;
  }, []);

  const removeEntry = useCallback((id: string) => {
    const all = load().filter((e) => e.id !== id);
    save(all);
    window.dispatchEvent(new Event(NOTEBOOK_EVENT));
  }, []);

  const clear = useCallback(() => {
    save([]);
    window.dispatchEvent(new Event(NOTEBOOK_EVENT));
  }, []);

  return { entries, addEntry, removeEntry, clear };
}
